import { FormikHelpers } from 'formik';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { useCreateERPJob } from '@/raptorjob/application/adapters/useCreateERPSingleDayJob';
import { ERPJobFormValues } from '../../../ERPJobValidationSchema';
import { CreateRaptorJobError400 } from '@/raptorjob/domain/job-create-type';
import { mapERPValidationErrorsToFormikErrors } from '@/raptorjob/application/utils/erpValidationErrorMapper';

interface UseERPJobMutationsProps {
  openCreatedJob: (id: string) => void;
}

export const useERPJobMutations = ({ openCreatedJob }: UseERPJobMutationsProps) => {
  const { showSnackbar, showErrorWithDetails } = useSnackbar();
  const createJobMutation = useCreateERPJob();

  const handleSubmit = async (values: ERPJobFormValues, actions: FormikHelpers<ERPJobFormValues>) => {
    actions.setSubmitting(true);

    try {
      const response = await createJobMutation.mutateAsync({ values });

      showSnackbar('Job Created Successfully', `Job #${response.id} has been created.`, 'success');
      openCreatedJob(response.id);
    } catch (error: unknown) {
      // Handle 400 errors with validationErrors (new format)
      const error400 = error as CreateRaptorJobError400;
      if (error400?.validationErrors && Array.isArray(error400.validationErrors) && error400.validationErrors.length > 0) {
        // Map backend validation errors to Formik field errors
        const formikErrors = mapERPValidationErrorsToFormikErrors(error400.validationErrors);

        // Set errors on Formik fields and mark them as touched
        for (const [fieldName, errorMessage] of Object.entries(formikErrors)) {
          actions.setFieldError(fieldName, errorMessage);
          actions.setFieldTouched(fieldName, true, false); // Mark field as touched to display error message
        }

        showErrorWithDetails(error, 'Failed to create job', 'Job Creation Failed');
        return;
      }

      // Handle 422 errors (old format with error.details)
      const error422 = error as { error?: { details?: Array<{ path: string; reason: string }>; code?: string }; message?: string };
      const details: Array<{ path: string; reason: string }> | undefined = error422?.error?.details;
      if (Array.isArray(details)) {
        details.forEach((d) => {
          const path = (d.path || '').replace(/^\./, '');
          actions.setFieldError(path as string, d.reason || 'Invalid');
          actions.setFieldTouched(path as string, true, false);
        });
        showSnackbar(
          'Job Creation Failed',
          error422?.error?.code === 'VALIDATION_ERROR'
            ? 'Please review the highlighted fields.'
            : 'An unexpected error occurred while creating the job. Please try again.',
          'error'
        );
        return;
      }

      // Generic error handling
      showSnackbar(
        'Job Creation Failed',
        error instanceof Error ? error.message : 'An unexpected error occurred while creating the job. Please try again.',
        'error'
      );
    } finally {
      actions.setSubmitting(false);
    }
  };

  return {
    handleSubmit
  };
};
